import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { LogOut, Users, CreditCard, BarChart3, Calendar, CalendarDays, Eye, Salad, FileText, Plus, X, UserCheck, Receipt, ShieldCheck } from "lucide-react";
import OneOnOneTab from "../components/admin/OneOnOneTab";
import MembershipsTab from "../components/admin/MembershipsTab";
import AssessmentsTab from "../components/admin/AssessmentsTab";
import AuditTab from "../components/admin/AuditTab";

const TABS = [
  { id: "stats", label: "Analytics", Icon: BarChart3 },
  { id: "memberships", label: "Memberships", Icon: Users },
  { id: "oneonone", label: "1-on-1 Clients", Icon: UserCheck },
  { id: "assessments", label: "Assessments", Icon: Receipt },
  { id: "leads", label: "Leads", Icon: Users },
  { id: "clients", label: "Clients", Icon: FileText },
  { id: "payments", label: "Purchases", Icon: CreditCard },
  { id: "bookings", label: "Bookings", Icon: Calendar },
  { id: "availability", label: "Availability", Icon: CalendarDays },
  { id: "audit", label: "Audit", Icon: ShieldCheck },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = [
  "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "11:30",
  "12:00", "13:00", "14:00", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00",
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  // Reflect the active admin tab in the browser tab title.
  useEffect(() => {
    const activeLabel = TABS.find((t) => t.id === tab)?.label;
    document.title = activeLabel ? `Admin · ${activeLabel}` : "Gain Time · Admin";
    return () => { document.title = "Gain Time Fitness"; };
  }, [tab]);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [clientPlans, setClientPlans] = useState([]);
  const [adminInvoices, setAdminInvoices] = useState([]);
  const [membershipRequests, setMembershipRequests] = useState([]);
  const [newPlan, setNewPlan] = useState({ member_id: "", plan_name: "", amount: "", billing_day: 1 });
  const [avail, setAvail] = useState({ weekly: [], blocked_dates: [], timezone_name: "America/Chicago" });
  const [saving, setSaving] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [s, l, p, b, a, mem, plans, inv, reqs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/payments"),
        api.get("/admin/bookings"),
        api.get("/admin/availability"),
        api.get("/admin/members"),
        api.get("/admin/client-plans"),
        api.get("/admin/invoices"),
        api.get("/admin/membership-requests"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setPayments(p.data);
      setBookings(b.data);
      setMembers(mem.data || []);
      setClientPlans(plans.data || []);
      setAdminInvoices(inv.data || []);
      setMembershipRequests(reqs.data || []);
      setAvail({
        weekly: a.data.weekly || [],
        blocked_dates: a.data.blocked_dates || [],
        timezone_name: a.data.timezone_name || "America/Chicago",
      });
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem("gt_token");
    localStorage.removeItem("gt_member_token");
    navigate("/");
  };

  const toggleSlot = (dayIdx, time) => {
    setAvail((prev) => {
      const weekly = [...(prev.weekly || [])];
      const i = weekly.findIndex((w) => w.day === dayIdx);
      if (i === -1) {
        weekly.push({ day: dayIdx, times: [time] });
      } else {
        const times = weekly[i].times || [];
        weekly[i] = {
          day: dayIdx,
          times: times.includes(time) ? times.filter((t) => t !== time) : [...times, time].sort(),
        };
      }
      return { ...prev, weekly };
    });
  };

  const hasSlot = (dayIdx, time) => {
    const w = (avail.weekly || []).find((x) => x.day === dayIdx);
    return !!w?.times?.includes(time);
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability", avail);
      toast.success("Availability saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const assignPlan = async (e) => {
    e.preventDefault();
    if (!newPlan.member_id || !newPlan.plan_name || !newPlan.amount) {
      toast.error("Pick member, name, and amount.");
      return;
    }
    try {
      await api.post("/admin/client-plans", {
        member_id: newPlan.member_id,
        plan_name: newPlan.plan_name,
        amount: Number(newPlan.amount),
        billing_day: Number(newPlan.billing_day) || 1,
      });
      toast.success("Plan assigned. Invoice created.");
      setNewPlan({ member_id: "", plan_name: "", amount: "", billing_day: 1 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign plan");
    }
  };

  const deactivatePlan = async (id) => {
    try {
      await api.delete(`/admin/client-plans/${id}`);
      toast.success("Plan deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [l.name, l.email, l.phone, l.interest, l.message].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-900 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="https://customer-assets.emergentagent.com/job_houston-training-hub/artifacts/5f1feyf5_1781429554579.png" alt="Gain Time Fitness" className="w-8 h-8 object-cover shrink-0" />
            <span className="font-display text-sm sm:text-lg tracking-wider truncate">
              <span className="hidden sm:inline">ADMIN · GAIN TIME</span>
              <span className="sm:hidden">ADMIN</span>
            </span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E5B43A] transition-colors shrink-0"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 flex items-center gap-3 border-t border-zinc-900">
          <div className="flex gap-1 overflow-x-auto flex-1 min-w-0">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap ${
                  tab === id ? "border-[#E5B43A] text-white" : "border-transparent text-zinc-500 hover:text-white"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <a
            href="/member/dashboard"
            data-testid="admin-open-membership"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-[0.2em] uppercase border border-[#E5B43A] text-[#E5B43A] hover:bg-[#E5B43A] hover:text-white transition-colors whitespace-nowrap shrink-0"
          >
            <Salad size={14} /> Open Membership
          </a>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-12 py-10">
        {tab === "stats" && stats && (
          <div data-testid="tab-stats">
            <h2 className="font-display text-3xl mb-6">Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              {[
                { k: stats.visits_total, l: "Site Visits", testid: "stat-visits" },
                { k: stats.visits_today, l: "Visits Today", testid: "stat-visits-today" },
                { k: stats.leads, l: "Leads", testid: "stat-leads" },
                { k: stats.bookings, l: "Bookings", testid: "stat-bookings" },
                { k: stats.payments_paid, l: "Paid Assessments", testid: "stat-paid" },
                { k: `$${stats.revenue.toFixed(2)}`, l: "Revenue", testid: "stat-revenue" },
              ].map((c) => (
                <div key={c.testid} className="border border-zinc-800 bg-[#141414] p-5" data-testid={c.testid}>
                  <div className="font-display text-3xl text-white">{c.k}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-2">{c.l}</div>
                </div>
              ))}
            </div>
            <div className="border border-zinc-800 bg-[#141414] p-6">
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">Interest Breakdown</div>
              {stats.interests.length === 0 ? (
                <p className="text-zinc-500 text-sm">No leads yet.</p>
              ) : (
                <div className="space-y-2">
                  {stats.interests.map((i, idx) => {
                    const max = Math.max(...stats.interests.map((x) => x.count));
                    const pct = (i.count / max) * 100;
                    return (
                      <div key={`${i.label}-${idx}`} className="flex items-center gap-4">
                        <div className="w-32 text-sm text-zinc-300 truncate">{i.label}</div>
                        <div className="flex-1 h-3 bg-[#0A0A0A] border border-zinc-800 relative">
                          <div className="absolute inset-y-0 left-0 bg-[#E5B43A]" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-10 text-right text-sm text-zinc-400">{i.count}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}


        {tab === "memberships" && (
          <MembershipsTab members={members} membershipRequests={membershipRequests} onRefresh={load} />
        )}

        {tab === "oneonone" && <OneOnOneTab />}

        {tab === "assessments" && <AssessmentsTab />}

        {tab === "audit" && <AuditTab />}

        {tab === "leads" && (
          <div data-testid="tab-leads">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-display text-3xl">Leads <span className="text-zinc-500 text-base">({filteredLeads.length})</span></h2>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, message..."
                className="bg-[#141414] border border-zinc-800 px-4 py-2 text-sm w-64 focus:outline-none focus:border-[#E5B43A]"
                data-testid="leads-search"
              />
            </div>
            <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                    <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                      <th className="text-left px-5 py-3 font-bold">Name</th>
                      <th className="text-left px-5 py-3 font-bold">Email</th>
                      <th className="text-left px-5 py-3 font-bold hidden lg:table-cell">Phone</th>
                      <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Interest</th>
                      <th className="text-left px-5 py-3 font-bold">Date</th>
                      <th className="text-left px-5 py-3 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 ? (
                      <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">No leads match.</td></tr>
                    ) : filteredLeads.map((l) => (
                      <tr key={l.id} className="border-b border-zinc-900 hover:bg-[#1a1a1a] transition-colors" data-testid={`lead-row-${l.id}`}>
                        <td className="px-5 py-4 font-semibold whitespace-nowrap">{l.name}</td>
                        <td className="px-5 py-4 text-zinc-300"><a href={`mailto:${l.email}`} className="hover:text-[#E5B43A]">{l.email}</a></td>
                        <td className="px-5 py-4 text-zinc-400 hidden lg:table-cell whitespace-nowrap">{l.phone || "—"}</td>
                        <td className="px-5 py-4 text-zinc-400 hidden md:table-cell whitespace-nowrap">{l.interest || "—"}</td>
                        <td className="px-5 py-4 text-zinc-500 text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                        <td className="px-5 py-4">
                          <button onClick={() => setSelectedLead(l)} className="inline-flex items-center gap-1 text-xs text-[#E5B43A] hover:underline" data-testid={`view-lead-${l.id}`}>
                            <Eye size={12} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedLead && (
              <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/70" onClick={() => setSelectedLead(null)} data-testid="lead-modal">
                <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg border border-zinc-800 bg-[#141414] p-8">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">Lead Detail</div>
                  <div className="font-display text-3xl mb-4">{selectedLead.name}</div>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Email</div><a href={`mailto:${selectedLead.email}`} className="text-zinc-200 hover:text-[#E5B43A] break-all">{selectedLead.email}</a></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Phone</div><div className="text-zinc-200">{selectedLead.phone || "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Interest</div><div className="text-zinc-200">{selectedLead.interest || "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Submitted</div><div className="text-zinc-200">{fmtDate(selectedLead.created_at)}</div></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Message</div>
                    <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                  <div className="mt-6 flex gap-3">
                    <a href={`mailto:${selectedLead.email}`} className="bg-[#E5B43A] text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors">Reply</a>
                    <button onClick={() => setSelectedLead(null)} className="border border-zinc-700 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-[#E5B43A]">Close</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "clients" && (
          <div data-testid="tab-clients">
            <h2 className="font-display text-3xl mb-4">Client Service Plans</h2>
            <p className="text-zinc-500 text-sm mb-6">Assign a recurring training plan to any registered member. An invoice auto-generates each month on the billing day.</p>

            <form onSubmit={assignPlan} className="border border-[#E5B43A] bg-[#141414] p-6 mb-8" data-testid="assign-plan-form">
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">Assign New Plan</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <select
                  value={newPlan.member_id}
                  onChange={(e) => setNewPlan({ ...newPlan, member_id: e.target.value })}
                  data-testid="plan-member-select"
                  className="md:col-span-2 bg-[#0F0F0F] border border-zinc-800 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E5B43A]"
                >
                  <option value="">— Select member —</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} · {m.email}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Plan name (e.g. Performance Athlete)"
                  value={newPlan.plan_name}
                  onChange={(e) => setNewPlan({ ...newPlan, plan_name: e.target.value })}
                  data-testid="plan-name-input"
                  className="md:col-span-2 bg-[#0F0F0F] border border-zinc-800 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E5B43A]"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="$ amount"
                  value={newPlan.amount}
                  onChange={(e) => setNewPlan({ ...newPlan, amount: e.target.value })}
                  data-testid="plan-amount-input"
                  className="bg-[#0F0F0F] border border-zinc-800 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E5B43A]"
                />
              </div>
              <div className="flex items-center gap-4 mt-3">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500">Billing Day
                  <input
                    type="number" min="1" max="28"
                    value={newPlan.billing_day}
                    onChange={(e) => setNewPlan({ ...newPlan, billing_day: e.target.value })}
                    data-testid="plan-day-input"
                    className="ml-2 bg-[#0F0F0F] border border-zinc-800 px-2 py-1 w-16 text-white"
                  />
                </label>
                <button type="submit" data-testid="assign-plan-submit" className="ml-auto inline-flex items-center gap-2 bg-[#E5B43A] text-black px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
                  <Plus size={14} /> Assign Plan
                </button>
              </div>
            </form>

            <h3 className="font-display text-xl mb-3">Active Plans ({clientPlans.filter((p) => p.active).length})</h3>
            <div className="border border-zinc-800 bg-[#141414] mb-10 overflow-hidden">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                    <th className="text-left px-5 py-3">Member</th>
                    <th className="text-left px-5 py-3">Plan</th>
                    <th className="text-left px-5 py-3">Amount</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Bill Day</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Started</th>
                    <th className="text-right px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {clientPlans.length === 0 ? (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-500 text-sm">No plans yet.</td></tr>
                  ) : clientPlans.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-900 hover:bg-[#1a1a1a]" data-testid={`client-plan-${p.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold">{p.member_name}</div>
                        <div className="text-xs text-zinc-500">{p.member_email}</div>
                      </td>
                      <td className="px-5 py-4">{p.plan_name}</td>
                      <td className="px-5 py-4">${Number(p.amount).toFixed(2)} <span className="text-xs text-zinc-500">/mo</span></td>
                      <td className="px-5 py-4 text-zinc-400 hidden md:table-cell">{p.billing_day}</td>
                      <td className="px-5 py-4 text-zinc-500 text-xs hidden md:table-cell">{new Date(p.started_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4 text-right">
                        {p.active ? (
                          <button onClick={() => deactivatePlan(p.id)} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#E5B43A]" data-testid={`deactivate-plan-${p.id}`}>
                            <X size={12} /> Deactivate
                          </button>
                        ) : (
                          <span className="text-[10px] tracking-widest uppercase text-zinc-600">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="font-display text-xl mb-3">All Invoices ({adminInvoices.length})</h3>
            <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                    <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                      <th className="text-left px-5 py-3">Plan</th>
                      <th className="text-left px-5 py-3">Period</th>
                      <th className="text-left px-5 py-3">Amount</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminInvoices.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500 text-sm">No invoices yet.</td></tr>
                    ) : adminInvoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-zinc-900 hover:bg-[#1a1a1a]" data-testid={`admin-invoice-${inv.id}`}>
                        <td className="px-5 py-4 font-semibold">{inv.plan_name}</td>
                        <td className="px-5 py-4 text-zinc-400">{inv.billing_period}</td>
                        <td className="px-5 py-4">${Number(inv.amount).toFixed(2)}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border ${
                            inv.status === "paid" ? "border-zinc-700 text-zinc-400" :
                            inv.status === "overdue" ? "bg-[#E5B43A] text-black border-[#E5B43A]" :
                            "border-[#E5B43A] text-[#E5B43A]"
                          }`}>{inv.status}</span>
                        </td>
                        <td className="px-5 py-4 text-zinc-500 text-xs">{new Date(inv.due_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="tab-payments">
            <h2 className="font-display text-3xl mb-4">Purchases <span className="text-zinc-500 text-base">({payments.length})</span></h2>
            <div className="border border-zinc-800 bg-[#141414]">
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                    <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                      <th className="text-left px-5 py-3 font-bold">Client</th>
                      <th className="text-left px-5 py-3 font-bold">Email</th>
                      <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Item</th>
                      <th className="text-left px-5 py-3 font-bold">Amount</th>
                      <th className="text-left px-5 py-3 font-bold">Status</th>
                      <th className="text-left px-5 py-3 font-bold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">No purchases yet.</td></tr>
                    ) : payments.map((p) => (
                      <tr key={p.session_id} className="border-b border-zinc-900 hover:bg-[#1a1a1a]" data-testid={`payment-row-${p.session_id}`}>
                        <td className="px-5 py-4 font-semibold whitespace-nowrap">{p.metadata?.name || "—"}</td>
                        <td className="px-5 py-4 text-zinc-300 break-all">{p.metadata?.email || "—"}</td>
                        <td className="px-5 py-4 text-zinc-400 hidden md:table-cell">Assessment</td>
                        <td className="px-5 py-4 whitespace-nowrap">${Number(p.amount).toFixed(2)}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border ${
                            p.payment_status === "paid" ? "border-[#E5B43A] text-[#E5B43A]" : "border-zinc-700 text-zinc-400"
                          }`}>{p.payment_status}</span>
                        </td>
                        <td className="px-5 py-4 text-zinc-500 text-xs whitespace-nowrap">{fmtDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="tab-bookings">
            <h2 className="font-display text-3xl mb-4">Bookings <span className="text-zinc-500 text-base">({bookings.length})</span></h2>
            <div className="border border-zinc-800 bg-[#141414]">
              <div className="max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                    <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                      <th className="text-left px-5 py-3 font-bold">Slot</th>
                      <th className="text-left px-5 py-3 font-bold">Name</th>
                      <th className="text-left px-5 py-3 font-bold">Email</th>
                      <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Phone</th>
                      <th className="text-left px-5 py-3 font-bold">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-500">No bookings yet.</td></tr>
                    ) : bookings.map((b) => (
                      <tr key={b.id} className="border-b border-zinc-900 hover:bg-[#1a1a1a]" data-testid={`booking-row-${b.id}`}>
                        <td className="px-5 py-4 font-semibold whitespace-nowrap">{fmtDate(b.slot_iso)}</td>
                        <td className="px-5 py-4">{b.name}</td>
                        <td className="px-5 py-4 text-zinc-300 break-all">{b.email}</td>
                        <td className="px-5 py-4 text-zinc-400 hidden md:table-cell">{b.phone || "—"}</td>
                        <td className="px-5 py-4">{b.paid ? <span className="text-[#E5B43A]">✓ Paid</span> : <span className="text-zinc-500">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "availability" && (
          <div data-testid="tab-availability">
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="font-display text-3xl">Weekly Availability</h2>
                <p className="text-zinc-400 text-sm mt-1">Tap a cell to toggle. Schedule recurs every week.</p>
              </div>
              <button
                onClick={saveAvailability}
                disabled={saving}
                data-testid="availability-save-button"
                className="bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-3 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60 self-start"
              >
                {saving ? "Saving..." : "Save Schedule"}
              </button>
            </div>
            <div className="border border-zinc-800 bg-[#141414] overflow-x-auto">
              <div className="grid min-w-[1100px]" style={{ gridTemplateColumns: `100px repeat(${HOURS.length}, minmax(56px, 1fr))` }}>
                <div className="p-3 border-b border-r border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Day</div>
                {HOURS.map((h) => (
                  <div key={h} className="p-2 border-b border-r border-zinc-800 text-center text-[10px] text-zinc-500">{h}</div>
                ))}
                {DAYS.map((d, idx) => (
                  <React.Fragment key={d}>
                    <div className="p-3 border-b border-r border-zinc-800 text-xs font-bold tracking-widest uppercase">{d}</div>
                    {HOURS.map((h) => (
                      <button
                        key={`${d}-${h}`}
                        onClick={() => toggleSlot(idx, h)}
                        data-testid={`slot-${idx}-${h}`}
                        className={`border-b border-r border-zinc-800 h-12 transition-colors ${
                          hasSlot(idx, h) ? "bg-[#E5B43A] hover:bg-[#FF6B63]" : "hover:bg-zinc-900"
                        }`}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <p className="mt-4 text-xs text-zinc-500">Timezone: {avail.timezone_name} · {(avail.weekly || []).reduce((n, w) => n + (w.times?.length || 0), 0)} active slots</p>
          </div>
        )}
      </main>

    </div>
  );
}
